import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';
import { useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import { useRouteParams } from '../../../hooks/useRouteParams';
import { useFinancialPlanQuery } from '../../../../api/useFinancialPlanQuery';
import { PlanActionStepDTO } from '../../../../types/planActionStepsDTO';
import { AsyncComponentHookResult } from '../../../../../Types/ComponentHookResult';
import { selectedGoalSelector } from '../../../../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { ActionStepType } from '../../../../types/actionStepTypeOptionDTO';
import { GoalDTO } from '../../../../types/goalsDTO';
import { PlanActionStepsAdvisersAssigneesDTO } from '../../../../types/planActionStepsAdvisersAssigneesDTO';

export const useActionSteps = (
  _goalId?: string,
): AsyncComponentHookResult<{
  actionSteps: PlanActionStepDTO[];
  goal: GoalDTO;
  advisorAssignees: PlanActionStepsAdvisersAssigneesDTO | null;
  showAssetBucketing: boolean;
  availableAssetBuckets: { Title: string }[];
}> => {
  const { clientId, goalId: routeGoalId } = useRouteParams();

  const goalId = _goalId ?? routeGoalId;

  const queryResult = useFinancialPlanQuery();

  /**
   * TODO (LEGACY): At the moment, the selected goal and sub-goal ids are stored in the legacy
   * FinancialPlanGoalDropdown reducer. We need to keep it there while the goals migration is
   * in progress.
   *
   * For the OPL version, we should perhaps add one more route params (subGoalTypeId), keeping the
   * selected goal and subGoal ids in the route instead.
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const selectedGoal = useSelector(state => selectedGoalSelector(state, { clientId }));

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const {
      PlanActionSteps,
      PlanActionStepsAdvisersAssignees,
      ActionStepAssetBucketOptions,
    } = queryResult.data;

    if (!PlanActionSteps || !selectedGoal) {
      return null;
    }

    const goalActionSteps = PlanActionSteps.filter(({ GoalId }) => GoalId === goalId);

    const filteredActionSteps = goalActionSteps.filter(actionStep => {
      if (
        !actionStep.IsTask ||
        actionStep.Type === ActionStepType.Suggestion ||
        actionStep.Type === ActionStepType.AdditionalTips
      ) {
        return false;
      }

      if (selectedGoal.isSubGoal) {
        return actionStep.DebtType === selectedGoal.DebtType;
      }

      // Don't render sub goals action steps on the parent goal
      return isNil(actionStep.DebtType);
    });

    return {
      actionSteps: sortBy(filteredActionSteps, item => new Date(item.CreatedOn), 'desc'),
      goal: selectedGoal,
      advisorAssignees: PlanActionStepsAdvisersAssignees,
      showAssetBucketing: ActionStepAssetBucketOptions.AllowAssetBucketingCustomization,
      availableAssetBuckets: ActionStepAssetBucketOptions.AvailableAssetBuckets,
    };
  }, [goalId, queryResult.data, selectedGoal]);

  return {
    ...queryResult,
    data,
  };
};
